import { useRef } from "react";
import { Printer } from "lucide-react";
import { cetak, rp } from "./FakturPrint";
import { formatTanggalPanjang } from "../../lib/tanggal";

// Cetak "Invoice Kredit" satu data kredit Claim MDR (brief owner 22 Sep 2026
// butir C.28, keputusan C13).
//
// KENAPA KOMPONEN SENDIRI, BUKAN FakturPrint
// -------------------------------------------
// Dokumen ini SENGAJA berbeda dari nota penjualan aslinya: Jumlah = Total =
// NOMINAL KREDIT (porsi yang dibiayai leasing), bukan nilai nota. Kalau
// dirender lewat FakturPrint, ia akan tampak seperti faktur penjualan dengan
// nomor yang sama tapi angka berbeda — dua dokumen bernomor sama yang saling
// membantah (risiko audit/sengketa di peta area C). Karena itu judulnya
// "INVOICE KREDIT" dan nomornya turunan `<no nota>/K` (dari server).
//
// Yang DIPAKAI ULANG: `cetak()` dari FakturPrint — menyalin `.print-area` ke
// `#print-root` anak langsung <body>, perbaikan keluhan "faktur keluar 3-4
// rangkap" (CLAUDE.md). `window.print()` mentah di sini akan melahirkan bug
// rangkap itu kembali, karena pratinjaunya duduk di modal `position: fixed`.
//
// Isi datang UTUH dari server (`GET /claim-mdr/.../invoice`): baris Harga Jual
// hanya ada kalau tab-nya mengaktifkan (bawaan GDN) — layar tidak memutuskan
// sendiri, supaya pengaturan tab yang diubah owner langsung berlaku di cetakan.

// A5 potret: dokumen satu unit, tidak butuh kertas A4. Nilainya dipakai di
// @page DAN lebar pratinjau, jadi keduanya tidak bisa berpisah jalan.
const KERTAS = { width: 148, height: 210 };
const MARGIN_MM = 10;

export default function InvoiceKreditPrint({ data }) {
  const ref = useRef(null);
  const d = data || {};
  const kop = d.kop || {};
  // Nama toko dari Branding (server) — BUKAN `kop.judul` template faktur, yang
  // di template bawaan berbunyi "FAKTUR PENJUALAN" (judul dokumen lain).
  const namaToko = d.nama_toko || "";
  const barisHarga = !!d.tampil_harga_jual;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button type="button" onClick={() => cetak(ref.current)} data-testid="mdr-invoice-cetak"
                className="h-10 px-4 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium inline-flex items-center gap-2">
          <Printer className="w-4 h-4" /> Cetak
        </button>
      </div>

      <style>{`@media print { @page { size: ${KERTAS.width}mm ${KERTAS.height}mm; margin: ${MARGIN_MM}mm; } }`}</style>
      <div ref={ref} data-testid="mdr-invoice-area"
           className="print-area bg-white text-slate-900 mx-auto border border-slate-200 shadow-sm w-full max-w-[148mm] p-6 text-[12px] leading-snug">
        <div className="flex items-start gap-3 border-b-2 border-slate-900 pb-2">
          {kop.tampil_logo !== false && kop.logo_url && (
            <img src={kop.logo_url} alt="" style={{ maxHeight: "44px" }} />
          )}
          <div className="flex-1 min-w-0">
            {namaToko && <p className="text-base font-bold leading-tight">{namaToko}</p>}
            {kop.alamat && <p className="text-[10px]">{kop.alamat}</p>}
            {kop.telepon && <p className="text-[10px]">{kop.telepon}</p>}
          </div>
        </div>

        <div className="text-center my-3">
          <p className="text-sm font-bold tracking-widest" data-testid="mdr-invoice-judul">
            {(d.judul || "Invoice Kredit").toUpperCase()}
          </p>
          <p className="text-[11px]" data-testid="mdr-invoice-nomor">No. {d.nomor || "-"}</p>
        </div>

        <table className="text-[11px] mb-3">
          <tbody>
            <tr><td className="pr-3 py-0.5 font-semibold">Tanggal Pembelian</td><td className="pr-2">:</td>
                <td>{d.tanggal_pembelian ? formatTanggalPanjang(d.tanggal_pembelian) : "-"}</td></tr>
            <tr><td className="pr-3 py-0.5 font-semibold">Nama Konsumen</td><td className="pr-2">:</td>
                <td data-testid="mdr-invoice-konsumen">{d.nama_konsumen || "-"}</td></tr>
            {d.cabang && (
              <tr><td className="pr-3 py-0.5 font-semibold">Cabang</td><td className="pr-2">:</td><td>{d.cabang}</td></tr>
            )}
          </tbody>
        </table>

        <table className="w-full text-[11px] border-collapse">
          <thead>
            <tr className="border-y border-slate-900">
              <th className="text-left py-1 pr-2">Tipe Produk</th>
              <th className="text-left py-1 pr-2">IMEI / Serial Number</th>
              <th className="text-right py-1">Jumlah</th>
            </tr>
          </thead>
          <tbody>
            <tr className="align-top">
              <td className="py-1 pr-2" data-testid="mdr-invoice-tipe">{d.tipe_produk || "-"}</td>
              <td className="py-1 pr-2 font-mono">
                <p>IMEI: {d.imei || "-"}</p>
                <p>SN: {d.sn || "-"}</p>
              </td>
              <td className="py-1 text-right font-mono" data-testid="mdr-invoice-jumlah">{rp(d.jumlah)}</td>
            </tr>
          </tbody>
        </table>

        <div className="border-t border-slate-900 mt-1 pt-1 space-y-0.5">
          {barisHarga && (
            <div className="flex justify-between" data-testid="mdr-invoice-harga-jual">
              <span>Harga Jual</span><span className="font-mono">{rp(d.harga_jual)}</span>
            </div>
          )}
          <div className="flex justify-between font-bold text-[12px]">
            <span>Total</span><span className="font-mono" data-testid="mdr-invoice-total">{rp(d.total)}</span>
          </div>
        </div>

        {(d.leasing || d.tenor || d.no_kontrak) && (
          <p className="mt-2 text-[10px] text-slate-700">
            Pembayaran kredit: {d.leasing || "-"}
            {d.tenor ? ` · ${d.tenor} bulan` : ""}
            {d.no_kontrak ? ` · No. Kontrak ${d.no_kontrak}` : ""}
          </p>
        )}
        <p className="mt-2 text-[11px] font-semibold" data-testid="mdr-invoice-garansi">{d.garansi || "Garansi 1 tahun"}</p>
        {kop.footer_teks && <p className="mt-2 text-[10px] leading-snug">{kop.footer_teks}</p>}

        <div className="mt-6 flex justify-end text-[11px]">
          <div className="text-center" style={{ minWidth: "40mm" }}>
            <p>{d.penutup || "Hormat kami,"}</p>
            <div style={{ height: "16mm" }} />
            <p className="border-t border-slate-900 pt-1">{namaToko || " "}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
